import { ehDecimal, formatarFracaoPercentual } from '@/lib/format';

export interface FatiaDonut {
  rotulo: string;
  /** Fracao da API ou digitada e convertida ("0.5" = 50%). */
  fracao: string;
}

/** Cores dos tokens do tema, em ciclo. */
const CORES = ['var(--primary)', 'var(--chart-lucro)', 'var(--chart-desp-variavel)', 'var(--chart-desp-fixa)', 'var(--chart-custo)', 'var(--muted-foreground)'];

/**
 * Rosca SVG das frações (so desenho: os percentuais exibidos vem de `formatarFracaoPercentual`). Usa pathLength=100, entao
 * cada fatia e um dash de `pct` sobre 100. Sem fatia valida, nao renderiza nada, a menos que `anelVazio` (anel cinza + "Sem proporções").
 */
export function Donut({ fatias, titulo = 'Distribuição por centro', anelVazio = false }: { fatias: FatiaDonut[]; titulo?: string; anelVazio?: boolean }) {
  const validas = fatias.filter((f) => ehDecimal(f.fracao));
  if (validas.length === 0 && !anelVazio) return null;

  let acumulado = 0;
  const arcos = validas.map((f, i) => {
    // Geometria do desenho (0 a 100), nao e valor de negocio.
    const pct = Math.min(Math.max(Number(f.fracao) * 100, 0), 100);
    const arco = { rotulo: f.rotulo, fracao: f.fracao, cor: CORES[i % CORES.length] ?? 'var(--primary)', pct, offset: 25 - acumulado, chave: `${i}-${f.rotulo}` };
    acumulado += pct;
    return arco;
  });

  const resumo = arcos.length > 0 ? arcos.map((a) => `${a.rotulo} ${formatarFracaoPercentual(a.fracao)}`).join(', ') : 'sem proporções';

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg role="img" aria-label={`${titulo}: ${resumo}`} viewBox="0 0 36 36" className="size-28 shrink-0">
        <circle cx="18" cy="18" r="15.9155" fill="none" strokeWidth="4" style={{ stroke: 'var(--line-soft)' }} />
        {arcos.map((a) => (
          <circle
            key={a.chave}
            cx="18"
            cy="18"
            r="15.9155"
            fill="none"
            strokeWidth="4"
            pathLength={100}
            strokeDasharray={`${a.pct} ${100 - a.pct}`}
            strokeDashoffset={a.offset}
            style={{ stroke: a.cor }}
          />
        ))}
      </svg>
      {arcos.length === 0 ? (
        <p className="min-w-0 flex-1 text-small text-ink-muted">Sem proporções</p>
      ) : (
        <ul className="min-w-0 flex-1 space-y-1 text-small">
          {arcos.map((a) => (
            <li key={a.chave} className="flex items-center gap-2">
              <span aria-hidden="true" className="size-2.5 shrink-0 rounded-full" style={{ background: a.cor }} />
              <span className="min-w-0 flex-1 truncate">{a.rotulo}</span>
              <span className="tabular">{formatarFracaoPercentual(a.fracao)}</span>
            </li>
          ))}
        </ul>
      )}
      <table className="sr-only">
        <caption>{titulo}</caption>
        <thead>
          <tr>
            <th scope="col">Centro</th>
            <th scope="col">Percentual</th>
          </tr>
        </thead>
        <tbody>
          {arcos.map((a) => (
            <tr key={a.chave}>
              <td>{a.rotulo}</td>
              <td>{formatarFracaoPercentual(a.fracao)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
